import { Controller } from "@hotwired/stimulus";

// Drives the two-column fare search form (bookings#search_fares):
//  - the loading overlay while the flight-search GET request is in flight
//  - the trip-type segmented control (oneway / round / multi) and its leg rows
//  - the traveller picker (search, select all / clear all, live summary)
//  - re-running the search when the travellers change, once a search has run
//
// Usage:
//   data-controller="fare-search"                                          on the wrapping container
//   data-action="submit->fare-search#validate submit->fare-search#loading" on the search <form>
//   data-fare-search-target="overlay"                                      on the overlay element
//   data-fare-search-target="submitButton"                                 on the submit button (optional, gets disabled)
export default class FareSearchController extends Controller {
  static targets = [
    "overlay",
    "overlayTitle",
    "overlayHint",
    "error",
    "paxError",
    "submitButton",
    "tripButton",
    "tripTypeInput",
    "legsContainer",
    "legRow",
    "legTemplate",
    "addLegButton",
    "paxSelectedCount",
    "paxBreakdown",
    "paxQuery",
    "paxRow",
    "paxCheckbox",
  ];

  static values = {
    paxAdult: Object,
    paxKid: Object,
    paxBaby: Object,
    paxNoSelection: String,
    legTitleOutbound: String,
    legTitleReturn: String,
    legTitleMulti: String,
    legDate: String,
    legDateReturn: String,
    searched: Boolean,
    loadingTitle: String,
    loadingHint: String,
    updatingTitle: String,
    updatingHint: String,
    bikesTitle: String,
    bikesHint: String,
    flexTitle: String,
    flexHint: String,
    failed: String,
    stepUrl: String,
    autostart: Boolean,
  };

  // Work per request in the follow-up steps: bike checks are one SOAP call per
  // leg per offer, flex prices one GetFares each (the server runs 4 at a time).
  static bikeBatchSize = 3;

  static flexBatchSize = 4;

  // Long enough to step a traveller count from 1 to 3 (or tick a few
  // travellers) and still end up with a single search.
  static autoSearchDelay = 1500;

  connect() {
    // Restore state when user navigates back via browser history
    this.hide();
    this.legCounter = this.hasLegRowTarget ? this.legRowTargets.length : 0;
    this.renumberLegs();
    this.updateSummary();
    this.syncReturnMin();
    // A page restored from the back/forward cache keeps its script state: the
    // search that left it is over.
    this.onPageShow = (event) => {
      if (!event.persisted) return;
      this.running = false;
      this.hide();
    };
    window.addEventListener("pageshow", this.onPageShow);
    if (this.autostartValue) this.runSearch();
  }

  disconnect() {
    window.removeEventListener("pageshow", this.onPageShow);
    clearTimeout(this.autoSearchTimer);
  }

  // The search runs in steps behind the loading modal, which says which one is
  // going on (see runSearch); the page is only left for the finished results.
  submit(event) {
    event.preventDefault();
    this.runSearch();
  }

  async runSearch() {
    // A manual submit supersedes a pending automatic one.
    clearTimeout(this.autoSearchTimer);
    if (this.running) return;

    this.running = true;
    this.clearError();
    this.show();
    this.updating = false;

    const form = this.element.querySelector("#fapi-search-form");
    const params = new URLSearchParams(new FormData(form));

    try {
      const first = await this.step("search", params);

      // A resync of the placeholder travellers gave them new ids.
      params.delete("participant_ids[]");
      first.participant_ids.forEach((id) =>
        params.append("participant_ids[]", id)
      );

      if (params.get("bike_required") === "1" && first.fare_ids.length)
        await this.checkBikes(params, first.fare_ids);

      const prices = first.flex_jobs.length
        ? await this.loadFlexPrices(params, first.flex_jobs)
        : {};

      await this.step(
        "finish",
        params,
        Object.entries(prices)
          .filter(([, price]) => price !== null)
          .map(([job, price]) => [`flex_prices[${job}]`, price])
      );

      params.set("fare_search", "done");
      window.location.replace(`${form.action}?${params}`);
    } catch (error) {
      this.fail(error.message || this.failedValue);
    }
  }

  async checkBikes(params, fareIds) {
    const size = FareSearchController.bikeBatchSize;
    for (let done = 0; done < fareIds.length; done += size) {
      this.setStatus(
        this.bikesTitleValue,
        this.bikesHintValue,
        done,
        fareIds.length
      );
      // eslint-disable-next-line no-await-in-loop
      await this.step(
        "bikes",
        params,
        fareIds.slice(done, done + size).map((id) => ["fare_ids[]", id])
      );
    }
  }

  async loadFlexPrices(params, jobs) {
    const size = FareSearchController.flexBatchSize;
    const prices = {};
    for (let done = 0; done < jobs.length; done += size) {
      this.setStatus(
        this.flexTitleValue,
        this.flexHintValue,
        done,
        jobs.length
      );
      // eslint-disable-next-line no-await-in-loop
      const answer = await this.step(
        "flex",
        params,
        jobs
          .slice(done, done + size)
          .map(([index, offset]) => ["flex_jobs[]", `${index}:${offset}`])
      );
      Object.assign(prices, answer.prices);
    }
    return prices;
  }

  // One request of the search (BookingsController#search_fares_step): the whole
  // form again, plus what this step works on.
  async step(name, params, extra = []) {
    const body = new URLSearchParams(params);
    body.set("step", name);
    extra.forEach(([key, value]) => body.append(key, value));

    const response = await fetch(this.stepUrlValue, {
      method: "POST",
      headers: {
        Accept: "application/json",
        "X-CSRF-Token":
          document.querySelector('meta[name="csrf-token"]')?.content || "",
      },
      body,
    });
    const answer = await response.json().catch(() => null);
    if (!response.ok || !answer?.ok)
      throw new Error(answer?.message || this.failedValue);

    return answer;
  }

  setStatus(title, hint, done, total) {
    if (this.hasOverlayTitleTarget) this.overlayTitleTarget.textContent = title;
    if (this.hasOverlayHintTarget)
      this.overlayHintTarget.textContent = hint
        .replace("%{done}", String(done))
        .replace("%{total}", String(total));
  }

  fail(message) {
    this.running = false;
    this.hide();
    if (!this.hasErrorTarget) return;

    this.errorTarget.textContent = message;
    this.errorTarget.classList.remove("hidden");
    this.errorTarget.scrollIntoView({ block: "nearest", behavior: "smooth" });
  }

  clearError() {
    if (this.hasErrorTarget) this.errorTarget.classList.add("hidden");
  }

  // Re-runs the search after the traveller selection or pax counts changed, so
  // the shown prices always match the selected travellers. Only once a search
  // has run on this page (searchedValue); before that a change just edits the
  // form. Debounced so ticking several travellers fires one search. Skipped
  // while nobody is selected: the server would read that as "everyone".
  autoSearch() {
    if (!this.searchedValue) return;
    if (this.hasPaxCheckboxTarget && !this.paxSelected()) return;

    clearTimeout(this.autoSearchTimer);
    this.autoSearchTimer = setTimeout(() => {
      const form = this.element.querySelector("#fapi-search-form");
      if (!form) return;

      // Tells loading() to explain why the page is reloading by itself.
      this.updating = true;
      form.requestSubmit();
    }, FareSearchController.autoSearchDelay);
  }

  // ── Validation ─────────────────────────────────────────────────────────
  // Client-side check before submit: required fields per visible leg, plus
  // whatever the server would otherwise catch silently (a blank field used to
  // reach the controller and just get the whole leg dropped — see
  // BookingsController#parsed_legs). This is a fast first pass, not a
  // replacement for the server-side check.
  validate(event) {
    this.clearFieldErrors();

    // A search without a traveller would silently be priced for everyone.
    if (this.hasPaxCheckboxTarget && !this.paxSelected()) {
      this.showPaxError();
      event.preventDefault();
      event.stopImmediatePropagation();
      return;
    }

    const rows = this.legRowTargets.filter(
      (row) => !row.classList.contains("hidden")
    );
    let firstInvalid = null;

    rows.forEach((row) => {
      ["from", "to", "date"].forEach((field) => {
        const el = this.legField(row, field);
        if (el && !el.disabled && !el.value.trim()) {
          this.markFieldInvalid(el);
          firstInvalid = firstInvalid || el;
        }
      });
    });

    if (firstInvalid) {
      event.preventDefault();
      event.stopImmediatePropagation();
      firstInvalid.focus();
    }
  }

  // eslint-disable-next-line class-methods-use-this
  markFieldInvalid(el) {
    el.classList.add(
      "border-red-400",
      "focus:border-red-400",
      "focus:ring-red-400"
    );
    el.classList.remove("border-gray-300");
  }

  clearFieldErrors() {
    this.legRowTargets.forEach((row) => {
      ["from", "to", "date"].forEach((field) => {
        const el = this.legField(row, field);
        if (el) {
          el.classList.remove(
            "border-red-400",
            "focus:border-red-400",
            "focus:ring-red-400"
          );
          el.classList.add("border-gray-300");
        }
      });
    });
  }

  // Round-trip return date can't be before the outbound date — the browser
  // date picker otherwise happily accepts it with nothing pointing out the
  // mistake until the combined search comes back empty (see U3, wiki Roadmap §7).
  syncReturnMin(event) {
    if (this.legRowTargets.length < 2) return;

    const outboundDate = this.legField(this.legRowTargets[0], "date");
    const returnDate = this.legField(this.legRowTargets[1], "date");
    if (!outboundDate || !returnDate) return;

    // Only the outbound date drives the minimum. Reacting to the return date's
    // own change events (it fires them while a year like "2026" is still being
    // typed) rewrote its min mid-edit, which made the browser reset the field.
    if (event?.target && event.target !== outboundDate) return;

    const min = outboundDate.value || "";
    if (returnDate.min !== min) returnDate.min = min;
  }

  show() {
    if (this.hasOverlayTitleTarget)
      this.overlayTitleTarget.textContent = this.updating
        ? this.updatingTitleValue
        : this.loadingTitleValue;
    if (this.hasOverlayHintTarget)
      this.overlayHintTarget.textContent = this.updating
        ? this.updatingHintValue
        : this.loadingHintValue;
    if (this.hasOverlayTarget) this.overlayTarget.classList.remove("hidden");
    if (this.hasSubmitButtonTarget) this.submitButtonTarget.disabled = true;
  }

  hide() {
    this.updating = false;
    if (this.hasOverlayTarget) this.overlayTarget.classList.add("hidden");
    if (this.hasSubmitButtonTarget) this.submitButtonTarget.disabled = false;
  }

  // ── Trip type ──────────────────────────────────────────────────────────

  setTrip(event) {
    const { trip } = event.currentTarget.dataset;
    if (!trip) return;

    this.tripTypeInputTarget.value = trip;
    this.applyTrip(trip);
  }

  applyTrip(trip) {
    this.tripButtonTargets.forEach((button) => {
      const active = button.dataset.trip === trip;
      button.setAttribute("aria-pressed", String(active));
      button.classList.toggle("bg-tourrise-blue", active);
      button.classList.toggle("text-white", active);
      button.classList.toggle("shadow-sm", active);
      button.classList.toggle("text-gray-500", !active);
    });

    if (trip === "oneway") {
      this.legRowTargets.forEach((row, i) => this.setRowVisible(row, i === 0));
    } else if (trip === "round") {
      if (this.legRowTargets.length < 2) this.addReturnLeg();
      this.legRowTargets.forEach((row, i) => this.setRowVisible(row, i < 2));
    } else {
      this.legRowTargets.forEach((row) => this.setRowVisible(row, true));
    }

    if (this.hasAddLegButtonTarget)
      this.addLegButtonTarget.classList.toggle("hidden", trip !== "multi");
    this.renumberLegs();
    this.syncReturnMin();
  }

  // eslint-disable-next-line class-methods-use-this
  setRowVisible(row, visible) {
    row.classList.toggle("hidden", !visible);
    row.querySelectorAll("input, select").forEach((field) => {
      const current = field;
      current.disabled = !visible;
    });
  }

  addLeg() {
    const lastRow = this.legRowTargets[this.legRowTargets.length - 1];
    const lastTo = this.legFieldValue(lastRow, "to");

    const row = this.cloneLegRow();
    this.setLegFieldValue(row, "from", lastTo);
    this.renumberLegs();
    this.legField(row, "from")?.focus();
  }

  addReturnLeg() {
    const outbound = this.legRowTargets[0];
    const from = this.legFieldValue(outbound, "from");
    const to = this.legFieldValue(outbound, "to");
    const cabin = this.legFieldValue(outbound, "cabin");

    const row = this.cloneLegRow();
    this.setLegFieldValue(row, "from", to);
    this.setLegFieldValue(row, "to", from);
    if (cabin) this.setLegFieldValue(row, "cabin", cabin);
  }

  removeLeg(event) {
    const row = event.currentTarget.closest(
      '[data-fare-search-target="legRow"]'
    );
    if (!row || this.legRowTargets.length <= 1) return;

    row.remove();
    this.renumberLegs();
  }

  swapLeg(event) {
    const row = event.currentTarget.closest(
      '[data-fare-search-target="legRow"]'
    );
    if (!row) return;

    const from = this.legField(row, "from");
    const to = this.legField(row, "to");
    if (!from || !to) return;

    [from.value, to.value] = [to.value, from.value];

    // Programmatic changes fire no input event: let the airport hints follow.
    [from, to].forEach((field) =>
      field.dispatchEvent(new CustomEvent("airport-hint:refresh"))
    );
  }

  cloneLegRow() {
    const fragment = this.legTemplateTarget.content.cloneNode(true);
    const index = this.legCounter;
    this.legCounter += 1;

    ["name", "id", "for"].forEach((attr) => {
      fragment.querySelectorAll(`[${attr}]`).forEach((el) => {
        el.setAttribute(
          attr,
          el.getAttribute(attr).replaceAll("__INDEX__", String(index))
        );
      });
    });

    this.legsContainerTarget.appendChild(fragment);
    return this.legRowTargets[this.legRowTargets.length - 1];
  }

  renumberLegs() {
    if (!this.hasTripTypeInputTarget) return;
    const trip = this.tripTypeInputTarget.value;
    const rows = this.legRowTargets;

    rows.forEach((row, index) => {
      const titleField = this.legField(row, "title");
      if (titleField) titleField.textContent = this.legTitleFor(trip, index);

      const dateLabelField = this.legField(row, "date-label");
      if (dateLabelField)
        dateLabelField.textContent = this.legDateLabelFor(trip, index);

      const removeField = this.legField(row, "remove");
      if (removeField)
        removeField.classList.toggle(
          "hidden",
          !(trip === "multi" && rows.length > 1)
        );
    });
  }

  legTitleFor(trip, index) {
    if (trip === "multi")
      return this.legTitleMultiValue.replace("%{number}", String(index + 1));
    return index === 0 ? this.legTitleOutboundValue : this.legTitleReturnValue;
  }

  legDateLabelFor(trip, index) {
    if (trip === "round" && index === 1) return this.legDateReturnValue;
    return this.legDateValue;
  }

  // eslint-disable-next-line class-methods-use-this
  legField(row, field) {
    return row?.querySelector(`[data-leg-field="${field}"]`);
  }

  legFieldValue(row, field) {
    return this.legField(row, field)?.value || "";
  }

  setLegFieldValue(row, field, value) {
    const el = this.legField(row, field);
    if (el) el.value = value;
  }

  // ── Travellers ─────────────────────────────────────────────────────────

  filterParticipants(event) {
    const query = event.target.value.trim().toLowerCase();

    this.paxRowTargets.forEach((row) => {
      const matches = !query || (row.dataset.name || "").includes(query);
      row.classList.toggle("hidden", !matches);
    });
  }

  selectAllParticipants() {
    this.paxCheckboxTargets.forEach((checkbox) => {
      const current = checkbox;
      current.checked = true;
    });
    this.updateSummary();
    this.autoSearch();
  }

  clearAllParticipants() {
    this.paxCheckboxTargets.forEach((checkbox) => {
      const current = checkbox;
      current.checked = false;
    });
    this.updateSummary();
    this.autoSearch();
  }

  paxSelected() {
    return this.paxCheckboxTargets.some((checkbox) => checkbox.checked);
  }

  showPaxError() {
    if (!this.hasPaxErrorTarget) return;

    this.paxErrorTarget.classList.remove("hidden");
    this.paxErrorTarget.scrollIntoView({
      block: "nearest",
      behavior: "smooth",
    });
  }

  updateSummary() {
    if (!this.hasPaxCheckboxTarget) return;

    if (this.hasPaxErrorTarget && this.paxSelected())
      this.paxErrorTarget.classList.add("hidden");

    const counts = { adult: 0, kid: 0, baby: 0 };
    let selected = 0;

    this.paxCheckboxTargets.forEach((checkbox) => {
      if (!checkbox.checked) return;
      selected += 1;
      const type = checkbox.dataset.participantType;
      if (counts[type] !== undefined) counts[type] += 1;
    });

    if (this.hasPaxSelectedCountTarget)
      this.paxSelectedCountTarget.textContent = String(selected);

    if (this.hasPaxBreakdownTarget) {
      const parts = [];
      if (counts.adult)
        parts.push(this.pluralize(this.paxAdultValue, counts.adult));
      if (counts.kid) parts.push(this.pluralize(this.paxKidValue, counts.kid));
      if (counts.baby)
        parts.push(this.pluralize(this.paxBabyValue, counts.baby));

      this.paxBreakdownTarget.textContent = parts.length
        ? parts.join(" · ")
        : this.paxNoSelectionValue;
    }
  }

  // eslint-disable-next-line class-methods-use-this
  pluralize(labels, count) {
    const template = count === 1 ? labels.one : labels.other;
    return template.replace("%{count}", String(count));
  }
}
